// Persist the most recent in-app route across iframe re-initialisations.
//
// Why a module instead of a React hook? The marketplace iframe is destroyed
// and recreated on every parent refresh, which means everything React-based
// inside it has to play catch-up. Doing the restore synchronously, before
// React mounts and HashRouter reads the URL, sidesteps a whole class of
// useEffect-ordering bugs.

const STORAGE_KEY = "ecerium.lastRoute";
// Don't restore stale routes — opening the app the next morning should land
// on the dashboard, not on whatever ticket was on screen yesterday.
const MAX_AGE_MS = 60 * 60 * 1000;

interface SavedRoute {
  path: string;
  ts: number;
}

export function saveRoute(path: string): void {
  if (!path) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path, ts: Date.now() } satisfies SavedRoute),
    );
  } catch {
    // Storage may be blocked (sandbox, private mode) — accept that refresh
    // won't restore.
  }
}

// Persist the ticket-list filter/sort/view state separately so the
// "Tickets" sidebar/bottom-nav link can take the user back to the last
// filtered view they had open, even if they navigated to /contacts in
// between. Stored under its own key so the route-memory restore on
// refresh stays focused on a single full path.
const TICKETS_URL_KEY = "ecerium.lastTicketsUrl";

export function saveTicketsUrl(path: string): void {
  if (!path) return;
  try {
    localStorage.setItem(TICKETS_URL_KEY, path);
  } catch { /* storage blocked — accept we lose the memory */ }
}

export function readTicketsUrl(): string {
  try {
    const v = localStorage.getItem(TICKETS_URL_KEY);
    if (typeof v === "string" && v.startsWith("/tickets")) return v;
  } catch { /* fall through */ }
  return "/tickets";
}

export function readRoute(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedRoute>;
    if (typeof parsed.path !== "string" || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed.path;
  } catch {
    return null;
  }
}

// Run synchronously before React mounts. If the current hash is empty (the
// marketplace just re-set the iframe src to the app's root URL) and we have a
// fresh saved route, splice it back into the URL so HashRouter picks it up
// during its first render.
export function restoreRouteBeforeMount(): void {
  if (typeof window === "undefined") return;
  const currentHash = window.location.hash.replace(/^#/, "");
  if (currentHash && currentHash !== "/" && currentHash !== "") return;
  const saved = readRoute();
  if (!saved || saved === "/") return;
  // history.replaceState avoids polluting the back stack with the bogus
  // root URL the marketplace just handed us.
  const target = saved.startsWith("/") ? saved : `/${saved}`;
  history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${target}`);
}
